<template>
    <div v-if="shortAmenitiesList">
        <h4 class="mb-3">Amenities</h4>
        <container class="text-muted">
            <row>
                <column :md="10">
                    <amenities-item v-for="(amenity, index) in firstHalfShortList" :key="`first-amenities-list-${amenity.id}-${index}`" class="mb-3" :amenity="amenity" />
                </column>
                <column :md="10">
                    <amenities-item v-for="(amenity, index) in secondHalfShortList" :key="`second-amenities-list-${amenity.id}-${index}`" class="mb-3" :amenity="amenity" />
                </column>
            </row>
        </container>

        <p v-if="showMore">
            <a href="#" @click.prevent="openShowAllModal">Show all {{ amenities.length }} amenities</a>
        </p>

        <modal v-if="showAmenitiesModal" size="xl" :open="amenitiesModalOpen" @close="closeShowAllModal">
            <template #header>
                Amenities
            </template>
            <template #default>
                <form-group style="max-width: 320px;">
                    <form-input type="search" v-model="keyword" placeholder="Search Amenities"/>
                </form-group>
                <container>
                    <row>
                        <column :md="8">
                            <amenities-item v-for="(amenity, index) in firstThirdList" :key="`full-1-amenities-list-${amenity.id}-${index}`" class="mb-3" :amenity="amenity" :includeSubAmenities="true" />
                        </column>
                        <column :md="8">
                            <amenities-item v-for="(amenity, index) in secondThirdList" :key="`full-2-amenities-list-${amenity.id}-${index}`" class="mb-3" :amenity="amenity" :includeSubAmenities="true" />
                        </column>
                        <column :md="8">
                            <amenities-item v-for="(amenity, index) in thirdThirdList" :key="`full-2-amenities-list-${amenity.id}-${index}`" class="mb-3" :amenity="amenity" :includeSubAmenities="true" />
                        </column>
                    </row>
                </container>
            </template>
            <template #buttons>
                <ui-button color="secondary" @click="amenitiesModalOpen=false" :underline="true" block="sm">
                    Close
                </ui-button>
            </template>
        </modal>
    </div>
</template>

<script type="text/javascript">
    import CommonComponents from '@/components/Common.vue';
    import AmenitiesItem from '@/components/Content/Public/Properties/Amenities/Item.vue';
    import ApiObject from '@/classes/ApiObject.js';
    import ApiConfig from '@/config/api.json';

    import { UseApiStore } from '@/store/Api.js';

    export default {
        components: {
            AmenitiesItem,
            ...CommonComponents,
        },

        setup() {
            return {
                apiStore: UseApiStore(),
            };
        },

        data() {
            return {
                error: null,
                disposed: false,
                emptyResults: null,
                requestsTimer: null,

                showAmenitiesModal: false,
                amenitiesModalOpen: true,
                
                keyword: null,
            };
        },

        created() {
            this.requestData();
        },

        beforeUnmount() {
            clearTimeout(this.requestsTimer);
            this.disposed = true;
        },

        props: {
            property: {
                type: ApiObject,
                required: true,
            },
        },

        computed: {
            requestUrl() {
                let url = 'properties/'+this.property.id+'/amenities';
                return url;
            },
            
            propertyWithAmenities() {
                let response = this.apiStore.requests[this.requestUrl];
                
                if (!response) {
                    return null;
                }

                if ((response.valid) && (response.results) && (!response.results.error)) {
                    return response.results;
                }
                else {
                    let error = 'Could not complete request';
                    if ((response.results) && (response.results.error)) {
                        if (response.results.error.message) error = response.results.error.message;
                        else error = response.results.error;
                    }
                    this.setError(error);
                }
                
                return null;
            },

            amenities() {
                if (this.propertyWithAmenities) {
                    let amenities = [];
                    let pools = [];
                    let outdoors = [];

                    let subPools = {};
                    let subAmenities = {};
                    let subOutdoors = {}
                    
                    if (
                        (this.propertyWithAmenities.publicPools) &&
                        (this.propertyWithAmenities.publicPools.length)
                    ) {
                        for (let i=0; i<this.propertyWithAmenities.publicPools.length; i++) {
                            let pool = this.propertyWithAmenities.publicPools[i];
                            if (pool.poolId) {
                                if (!subPools[pool.poolId]) {
                                    subPools[pool.poolId] = [];
                                }
                                subPools[pool.poolId].push(pool);
                            }
                            else {
                                pools.push(pool);
                            }
                        }
                    }

                    if (
                        (this.propertyWithAmenities.publicAmenities) &&
                        (this.propertyWithAmenities.publicAmenities.length)
                    ) {
                        for (let i=0; i<this.propertyWithAmenities.publicAmenities.length; i++) {
                            let amenity = this.propertyWithAmenities.publicAmenities[i];
                            if (amenity.amenityId) {
                                if (!subAmenities[amenity.amenityId]) {
                                    subAmenities[amenity.amenityId] = [];
                                }
                                subAmenities[amenity.amenityId].push(amenity);
                            }
                            else {
                                amenities.push(amenity);
                            }
                        }
                    }

                    if (
                        (this.propertyWithAmenities.publicOutdoors) &&
                        (this.propertyWithAmenities.publicOutdoors.length)
                    ) {
                        for (let i=0; i<this.propertyWithAmenities.publicOutdoors.length; i++) {
                            let outdoor = this.propertyWithAmenities.publicOutdoors[i];
                            if (outdoor.outdoorId) {
                                if (!subOutdoors[outdoor.outdoorId]) {
                                    subOutdoors[outdoor.outdoorId] = [];
                                }
                                subOutdoors[outdoor.outdoorId].push(outdoor);
                            }
                            else {
                                outdoors.push(outdoor);
                            }
                        }
                    }

                    // add the sub items
                    for (let i=0; i<pools.length; i++) {
                        if (subPools[pools[i].id]) {
                            pools[i].subItems = subPools[pools[i].id];
                        }
                    }
                    for (let i=0; i<amenities.length; i++) {
                        if (subAmenities[amenities[i].id]) {
                            amenities[i].subItems = subAmenities[amenities[i].id];
                        }
                    }
                    for (let i=0; i<outdoors.length; i++) {
                        if (subOutdoors[outdoors[i].id]) {
                            outdoors[i].subItems = subOutdoors[outdoors[i].id];
                        }
                    }

                    let re = [...pools, ...amenities, ...outdoors];

                    if (re.length) {
                        return re;
                    }
                }

                return null;
            },

            shortAmenitiesList() {
                if (this.amenities) {
                    let arr = [];
                    for (let i=0; i<this.amenities.length; i++) {
                        arr.push(this.amenities[i]);
                        if (arr.length == 10) break;
                    }

                    return arr;
                }

                return null;
            },

            firstHalfShortList() {
                if (this.shortAmenitiesList) {
                    let num = Math.ceil(this.shortAmenitiesList.length / 2);
                    return this.shortAmenitiesList.slice(0, num);
                }

                return null;
            },

            secondHalfShortList() {
                if (this.shortAmenitiesList) {
                    let num = Math.ceil(this.shortAmenitiesList.length / 2);
                    return this.shortAmenitiesList.slice(num);
                }

                return null;
            },

            showMore() {
                if ((this.amenities) && (this.amenities.length > this.shortAmenitiesList.length)) {
                    return true;
                }
                return false;
            },

            filteredAmenities() {

                if (this.keyword) {
                    let re = [];
                    let search = this.keyword.toLowerCase();
                    let searchTv = false;
                    let searchFridge = false;
                    if ((search == 'tv') || (search == 'television') || (search.indexOf('tel') != -1)) {
                        searchTv = true;
                    }
                    else if ((search.indexOf('frid') != -1) || (search == 'refrigerator')) {
                        searchFridge = true;
                    }

                    for (let i=0; i<this.amenities.length; i++) {

                        let compareName = this.amenities[i].name.toLowerCase();
                        if ((searchTv) && ((compareName.indexOf('tv') != -1) || (compareName.indexOf('television') != -1))) {
                            re.push(this.amenities[i]);
                        }
                        else if ((searchFridge) && ((compareName.indexOf('fridge') != -1) || (compareName.indexOf('refrigerator') != -1))) {
                            re.push(this.amenities[i]);
                        }
                        else if (this.amenities[i].name.toLowerCase().indexOf(search) != -1) {
                            re.push(this.amenities[i]);
                        }
                    }

                    if (re.length) {
                        return re;
                    }
                    return null;
                }

                return this.amenities;
            },

            firstThirdList() {
                if (this.filteredAmenities) {
                    if (this.filteredAmenities.length <= 3) {
                        return this.filteredAmenities;
                    }
                    let num = Math.ceil(this.filteredAmenities.length / 3);
                    return this.filteredAmenities.slice(0, num);
                }
                return null;
            },

            secondThirdList() {
                if ((this.filteredAmenities) && (this.filteredAmenities.length > 3)) {
                    let num = Math.ceil(this.filteredAmenities.length / 3);
                    return this.filteredAmenities.slice(num, num * 2);
                }
                return null;
            },

            thirdThirdList() {
                if ((this.filteredAmenities) && (this.filteredAmenities.length > 3)) {
                    let num = Math.ceil(this.filteredAmenities.length / 3);
                    return this.filteredAmenities.slice(num * 2);
                }
                return null;
            },
            
        },

        methods: {

            openShowAllModal() {
                this.amenitiesModalOpen = true;
                this.showAmenitiesModal = true;
            },

            closeShowAllModal() {
                this.showAmenitiesModal = false;
                this.amenitiesModalOpen = true;
            },

            async requestData() {
                if (this.disposed) return;
                
                this.setError(null);
                this.setEmptyResults(null);
                
                clearTimeout(this.requestsTimer);

                await this.apiStore.get(this.requestUrl);

                this.requestsTimer = setTimeout(this.requestData, ApiConfig.dashboardLongCache * 1000);
            },

            setError(error) {
                this.error = error;
            },
            
            setEmptyResults(val) {
                this.emptyResults = val;
            },
        }
    };

</script>
